/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

import { countryCodes } from '../translations/countryCodes';
import { getBrandProcessCash, getDefaultProcessAlias } from '../transactions/transactionHelpers';

// NOTE: if you want to change the structure of the data,
// you should also check src/util/configHelpers.js
// some validation is added there.

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - includeForListingTypes:        An array of listing types, for which the extended
 *   (optional)                     data is relevant and should be added.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Flex CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
export const listingFields = [
  {
    key: 'type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { label: 'Tops', option: 'tops' },
      { label: 'Jumpsuits', option: 'jumpsuits' },
      { label: 'Bodysuits', option: 'bodysuits' },
      { label: 'Röcke', option: 'skirts' },
      { label: 'Hosen', option: 'pants' },
      { label: 'Accessories', option: 'accessories' },
      { label: 'Kleider', option: 'dresses' },
      { label: 'Leggings', option: 'leggings' },
      { label: 'Pullover', option: 'sweater' },
      { label: 'Cardigans', option: 'cardigans' },
    ],
    filterConfig: {
      indexForSearch: true,
      group: 'primary',
      label: 'Art',
    },
    showConfig: {
      label: 'Art',
    },
    saveConfig: {
      label: 'Art',
    },
  },
  {
    key: 'size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { label: 'XXS', option: 'xxs' },
      { label: 'XS', option: 'xs' },
      { label: 'S', option: 's' },
      { label: 'M', option: 'm' },
      { label: 'L', option: 'l' },
      { label: 'XL', option: 'xl' },
      { label: 'XXL', option: 'xxl' },
      { label: 'Einheitsgröße', option: 'one_size' },
    ],
    filterConfig: {
      indexForSearch: true,
      group: 'primary',
      label: 'Größe',
    },
    showConfig: {
      label: 'Größe',
    },
    saveConfig: {
      isRequired: true,
      label: 'Größe',
    },
  },
  {
    key: 'condition',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      {
        label: 'Neu mit Etikett',
        option: 'new_with_tags',
      },
      {
        label: 'Wie neu',
        option: 'like_new',
      },
      {
        label: 'Großartig',
        option: 'great',
      },
      {
        label: 'Gut',
        option: 'good',
      },
    ],
    filterConfig: {
      indexForSearch: true,
      group: 'primary',
      label: 'Zustand',
    },
    showConfig: {
      label: 'Zustand',
    },
    saveConfig: {
      isRequired: true,
      label: 'Zustand',
    },
  },
  // {
  //   key: 'colour',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     {
  //       label: 'Terrakotta',
  //       option: ['terracotta', 'olive'],
  //     },
  //     // {
  //     //   label: 'Olive',
  //     //   option: 'olive',
  //     // },

  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     group: 'primary',
  //     label: 'Farbe',
  //   },
  //   showConfig: {
  //     label: 'Farbe',
  //   },
  //   saveConfig: {
  //     isRequired: true,
  //     label: 'Farbe',
  //   },
  // },
  // {
  //   key: 'colour',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { label: 'Schwarz', option: 'black' },
  //     { label: 'Weiß', option: 'white' },
  //     { label: 'Grau', option: 'gray' },
  //     { label: 'Rot/Pink', option: 'red_pink' },
  //     { label: 'Blau', option: 'blue' },
  //     { label: 'Grün', option: 'green' },
  //     { label: 'Gelb/Orange', option: 'yellow_orange' },
  //     { label: 'Lila', option: 'purple' },
  //     { label: 'Braun/Beige', option: 'brown_beige' },
  //     { label: 'Gemischt', option: 'multicolor' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     group: 'primary',
  //     label: 'Farbe',
  //     filterFn: (value, option) => {
  //       const colorMap = {
  //         black: ['Black', 'Black / Off White', 'Black & Off White'],
  //         white: ['Off White', 'Off-White', 'White', 'Natural White'],
  //         gray: ['Light Grey Melange', 'Steel Blue melange', 'Charcoal'],
  //         red_pink: ['Cranberry', 'Lipstick Red', 'Berry Pink', 'Pink', 'Cherry', 'Rusty Red'],
  //         blue: ['Dark Navy', 'Midnight Blue', 'Steel Blue melange', 'Moonlight Blue'],
  //         green: ['Thyme', 'Eukalypto', 'Forest Green'],
  //         yellow_orange: ['GOLD', 'Orange/Pink', 'Yellow Print'],
  //         purple: ['Pastel Lilac', 'Lilac', 'Lavendula', 'Purple', 'Fig'],
  //         brown_beige: [
  //           'Dark Taupe',
  //           'Sand',
  //           'Beige',
  //           'Oat Milk',
  //           'Nougat',
  //           'Beige melange',
  //           'Terracotta',
  //           'Khaki',
  //         ],
  //         multicolor: [
  //           'Multicolor',
  //           'Tiger',
  //           'Blue Daisy Print',
  //           'Black & White Floral',
  //           'Cubist Print',
  //         ],
  //       };
  //       return colorMap[option].includes(value);
  //     },
  //   },
  //   showConfig: {
  //     label: 'Farbe',
  //   },
  //   saveConfig: {
  //     label: 'Farbe',
  //   },
  // },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: With first iteration of hosted configs, we are unlikely to support
//         multiple listing types, even though this template has some
//         rudimentary support for it.
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem' and 'multipleItems'.
 *                        Default: 'multipleItems'.
 * - defaultListingFields This is relevant only to listings using default-inquiry process atm.
 *                        It contains price: true/false value to indicate, whether price should be shown.
 *                        If defaultListingFields.price is not explicitly set to _false_, price will be shown.
 */
const cashBrandTransactionProcess = getBrandProcessCash();
const alias = getDefaultProcessAlias();

export const listingTypes = [
  {
    listingType: 'product',
    label: 'Purchase cash',
    transactionType: {
      process: cashBrandTransactionProcess,
      alias: `${cashBrandTransactionProcess}/${alias}`,
      unitType: 'item',
    },
    stockType: 'oneItem',
    defaultListingFields: {
      price: true,
    },
  },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;
